/*
  Hero banner slider (sections/hero-banner.liquid).
  - Endless in both directions: the first and the last slide are cloned at the opposite ends,
    and once the slider comes to rest on a clone it is moved to the matching real slide without
    animation. Mobile browsers ignore such a move while the finger is down or the momentum of a
    swipe is still running, so the move waits for the scrolling to stop and is verified afterwards.
  - Swipe and auto-rotation only, no controls. Auto-rotation pauses on touch, hover, focus and
    while the tab is hidden, and is off for visitors who ask for reduced motion.
  - A personal LINE link cannot prefill a message, so the message is copied to the clipboard
    before LINE opens (the same as snippets/live-video-request.liquid).
*/
if (!customElements.get('hero-banner-slider')) {
  customElements.define(
    'hero-banner-slider',
    class HeroBannerSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.hero-banner__slider');
        if (!this.track) return;

        this.slideCount = this.track.children.length;
        this.index = 0;
        this.touching = false;
        this.lastScrollLeft = -1;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplaySpeed = (parseFloat(this.dataset.speed) || 8) * 1000;

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        if (this.slideCount < 2) return;

        this.addClones();
        this.setScrollLeft(this.slideWidth);

        this.checkSettled = this.checkSettled.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);

        this.track.addEventListener('scroll', () => this.scheduleSettleCheck(), { passive: true });
        if ('onscrollend' in window) {
          this.track.addEventListener('scrollend', this.checkSettled, { passive: true });
        }

        this.track.addEventListener('touchstart', () => this.onTouchStart(), { passive: true });
        ['touchend', 'touchcancel'].forEach((name) =>
          this.track.addEventListener(name, () => this.onTouchEnd(), { passive: true })
        );

        this.addEventListener('pointerenter', () => this.pause());
        this.addEventListener('pointerleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.play());
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        // The images settle the layout only once they are loaded, so the starting position is set again.
        window.addEventListener('load', this.checkSettled, { once: true });

        this.resizeObserver = new ResizeObserver(() => {
          if (!this.touching) this.setScrollLeft((this.index + 1) * this.slideWidth);
        });
        this.resizeObserver.observe(this.track);

        this.play();
      }

      disconnectedCallback() {
        this.pause();
        clearTimeout(this.settleTimeout);
        this.resizeObserver?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      get slideWidth() {
        return this.track.clientWidth;
      }

      // A clone of the last slide before the first one and a clone of the first slide after the last one.
      addClones() {
        const slides = Array.from(this.track.children);
        const first = slides[0].cloneNode(true);
        const last = slides[slides.length - 1].cloneNode(true);

        [first, last].forEach((clone) => {
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        });

        this.track.append(first);
        this.track.prepend(last);
      }

      // Moving without animation: a mobile browser can drop the new position, so it is checked twice.
      setScrollLeft(target, attemptsLeft = 3) {
        const track = this.track;
        const previousBehavior = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = target;
        track.style.scrollBehavior = previousBehavior;
        this.lastScrollLeft = track.scrollLeft;

        requestAnimationFrame(() => {
          if (this.touching || Math.abs(track.scrollLeft - target) < 2) return;
          if (attemptsLeft > 0) {
            this.setScrollLeft(target, attemptsLeft - 1);
          } else {
            // Last resort: a browser that keeps dropping the silent move still gets the slide shown.
            track.scrollTo({ left: target, behavior: 'smooth' });
          }
        });
      }

      // Moving with animation, the target may be a clone: the slider is put right once it comes to rest.
      goTo(index) {
        this.index = index;
        this.track.scrollTo({
          left: (index + 1) * this.slideWidth,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
        this.scheduleSettleCheck();
      }

      onTouchStart() {
        this.touching = true;
        this.pause();
      }

      onTouchEnd() {
        this.touching = false;
        this.scheduleSettleCheck();
        this.play();
      }

      scheduleSettleCheck() {
        clearTimeout(this.settleTimeout);
        this.settleTimeout = setTimeout(this.checkSettled, 150);
      }

      // The position is only put right once the finger is up and the scrolling has really stopped.
      checkSettled() {
        const width = this.slideWidth;
        if (!width) return;

        if (this.touching || this.track.scrollLeft !== this.lastScrollLeft) {
          this.lastScrollLeft = this.track.scrollLeft;
          this.scheduleSettleCheck();
          return;
        }

        const position = Math.round(this.track.scrollLeft / width) - 1;
        const slide = ((position % this.slideCount) + this.slideCount) % this.slideCount;
        this.index = slide;

        // On a clone: step over to the same real slide, invisibly.
        if (slide !== position) this.setScrollLeft((slide + 1) * width);

        // A swipe restarts the countdown, so a slide is never cut short.
        if (this.autoplay) this.play();
      }

      onVisibilityChange() {
        if (document.visibilityState === 'hidden') {
          this.pause();
        } else {
          this.play();
        }
      }

      play() {
        if (this.dataset.autoplay !== 'true' || this.slideCount < 2) return;
        if (this.reducedMotion.matches || this.touching || document.visibilityState === 'hidden') return;
        this.pause();
        this.autoplay = setInterval(() => this.goTo(this.index + 1), this.autoplaySpeed);
      }

      pause() {
        clearInterval(this.autoplay);
        this.autoplay = null;
      }

      onClick(event) {
        const link = event.target.closest('a[data-copy-message]');
        if (!link || !navigator.clipboard?.writeText) return;

        let message = '';
        try {
          message = JSON.parse(this.querySelector('[data-hero-banner-message]')?.textContent || '""');
        } catch (error) {
          return;
        }
        if (!message) return;

        navigator.clipboard
          .writeText(message)
          .then(() => {
            const note = this.querySelector('.hero-banner__copied');
            if (!note) return;
            note.hidden = false;
            clearTimeout(this.copiedTimeout);
            this.copiedTimeout = setTimeout(() => (note.hidden = true), 5000);
          })
          .catch(() => {});
      }
    }
  );
}
